import { Switch, Button, Tooltip, Space, Tag, Typography, Badge } from "antd";
import {
  CheckSquareOutlined,
  CloseOutlined,
  AppstoreAddOutlined,
  StarFilled,
  TagsOutlined,
} from "@ant-design/icons";
import { STORES as BLOG_STORES } from "../BlogsTable/stores";

const NA_CELL = <span style={{ color: "#999" }}>NA</span>;

const isValidVariantId = (v) => {
  if (v === null || v === undefined || v === "") return false;
  const n = Number(v);
  return Number.isInteger(n) && n > 0;
};

const labelForHash = (hash) =>
  BLOG_STORES.find((s) => s.hash === hash)?.label || "";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB");
};

const numFromCurrency = (s) => {
  if (!s) return 0;
  const n = parseFloat(String(s).replace(/[^0-9.\-]/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const buildColumns = ({ onOpenDetail, onManageBucket, onManageTags, onToggleActive, storeHashResolver, bucketsById = {}, productTypesById = {}, assignmentMap = {}, tagCountMap = {} }) => [
  {
    title: "SAP SKU",
    dataIndex: "sapSku",
    width: 120,
    ellipsis: true,
    fixed: "left",
    sorter: (a, b) => (a.sapSku || "").localeCompare(b.sapSku || ""),
    render: (_, record) =>
      record.bcSku && record.bcSku !== "NA" ? (
        <Button type="link" style={{ padding: 0 }} onClick={() => onOpenDetail?.(record)}>
          {record.bcSku}
        </Button>
      ) : (
        NA_CELL
      ),
  },
  {
    title: "SAP Name",
    dataIndex: "sapName",
    width: 220,
    fixed: "left",
    sorter: (a, b) => (a.sapName || "").localeCompare(b.sapName || ""),
    render: (v, record) => (
      <Space size={4} wrap={false} align="center">
        <span>{v || NA_CELL}</span>
        {Number(record.isDefault) === 1 && (
          <Tag color="gold" icon={<StarFilled />} style={{ marginRight: 0 }}>
            Default
          </Tag>
        )}
      </Space>
    ),
  },
  {
    title: "SAP Cust #",
    dataIndex: "sapCustomerNumber",
    width: 120,
    ellipsis: true,
    render: () => NA_CELL,
  },
  {
    title: "SAP Price",
    dataIndex: "sapPrice",
    width: 110,
    ellipsis: true,
    render: () => NA_CELL,
  },
  {
    title: "SAP MSRP",
    dataIndex: "sapMsrp",
    width: 120,
    ellipsis: true,
    onCell: (record) => ({
      style: {
        backgroundColor:
          numFromCurrency(record.sapMsrp) === 0 ? "rgba(255, 0, 0, 0.1)" : "transparent",
      },
    }),
    render: (v) => v || NA_CELL,
  },
  {
    title: "Available Stock",
    dataIndex: "availableStock",
    width: 150,
    ellipsis: true,
    onCell: (record) => ({
      style: {
        backgroundColor:
          Number(record.availableStock) < 5 ? "rgba(255, 0, 0, 0.1)" : "transparent",
      },
    }),
    render: (v) => v ?? 0,
  },
  {
    title: "Reserved Stock",
    dataIndex: "reservedStock",
    width: 150,
    ellipsis: true,
    onCell: (record) => ({
      style: {
        backgroundColor:
          Number(record.reservedStock) < 5 ? "rgba(255, 0, 0, 0.1)" : "transparent",
      },
    }),
    render: (v) => v ?? 0,
  },
  {
    title: "BC SKU",
    dataIndex: "bcSku",
    width: 100,
    ellipsis: true,
    sorter: (a, b) => (a.bcSku || "").localeCompare(b.bcSku || ""),
    render: (v) => v || NA_CELL,
  },
  {
    title: "Type",
    dataIndex: "type",
    width: 100,
    ellipsis: true,
    sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
    render: (v) => v || NA_CELL,
  },
  {
    title: "Active on Store",
    dataIndex: "active",
    width: 150,
    ellipsis: true,
    sorter: (a, b) => (a.active === b.active ? 0 : a.active ? 1 : -1),
    render: (_, record) => (
      <Switch
        checked={!!record.active}
        onChange={(checked) => onToggleActive?.(record, checked)}
      />
    ),
  },
  {
    title: "Store",
    dataIndex: "store",
    width: 90,
    ellipsis: true,
    sorter: (a, b) => (a.store || "").localeCompare(b.store || ""),
  },
  {
    title: "BC Inventory",
    dataIndex: "bcInventory",
    width: 130,
    ellipsis: true,
    sorter: (a, b) => Number(a.bcInventory || 0) - Number(b.bcInventory || 0),
    render: (v) => (v === "NA" ? NA_CELL : v ?? 0),
  },
  {
    title: "Subscription",
    dataIndex: "subscription",
    width: 130,
    ellipsis: true,
    sorter: (a, b) => (a.subscription || "").localeCompare(b.subscription || ""),
  },
  {
    title: "Enrichment",
    dataIndex: "enrichment",
    width: 130,
    ellipsis: true,
    sorter: (a, b) => (a.enrichment === b.enrichment ? 0 : a.enrichment ? 1 : -1),
    render: (text) =>
      text !== "" ? (
        text ? (
          <CheckSquareOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        )
      ) : (
        NA_CELL
      ),
  },
  {
    title: "Last Updated",
    dataIndex: "lastUpdated",
    width: 200,
    ellipsis: true,
    sorter: (a, b) => {
      const parse = (s) => {
        if (!s) return 0;
        const [date, time] = s.split(", ");
        if (!date || !time) return 0;
        const [d, m, y] = date.split("/").map(Number);
        const [hh, mm, ss] = time.split(":").map(Number);
        return new Date(y, (m || 1) - 1, d, hh || 0, mm || 0, ss || 0).getTime();
      };
      return parse(a.lastUpdated) - parse(b.lastUpdated);
    },
  },
  {
    title: "Type / Bucket",
    key: "typeBucket",
    width: 180,
    ellipsis: true,
    render: (_, record) => {
      const variantId = record?.raw?.variant_id;
      const assignment = variantId ? assignmentMap[String(variantId)] : null;

      const typeName = assignment?.typeName || null;
      const bucketId = assignment?.bucketId ?? null;
      const bucket = bucketId ? bucketsById[String(bucketId)] : null;
      const bucketLabel = bucket ? `${bucket.displayName}` : null;

      if (!typeName && !bucketLabel) return NA_CELL;

      return (
        <Space direction="vertical" size={2} style={{ lineHeight: 1.4 }}>
          {typeName && <Tag color="purple" style={{ marginRight: 0 }}>{typeName}</Tag>}
          {bucketLabel && <Tag color="cyan" style={{ marginRight: 0 }}>{bucketLabel}</Tag>}
        </Space>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    width: 100,
    fixed: "right",
    align: "center",
    render: (_, record) => {
      const variantId = record?.raw?.variant_id;
      const variantOk = isValidVariantId(variantId);
      const storeHash = storeHashResolver?.(record);
      const storeOk = !!storeHash;
      const enabled = variantOk && storeOk;
      const reason = !variantOk
        ? "No variant_id — bucket assignment requires a positive BC variant id"
        : !storeOk
        ? `Store "${record.store}" has no BC hash mapped`
        : null;
      const tagCount = tagCountMap[String(variantId)] ?? record.tagCount ?? 0;
      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
             <Tooltip title={reason || "Manage keyword tags"}>
            <Badge count={tagCount} size="small" offset={[-2, 2]}>
              <Button
                size="small"
                icon={<TagsOutlined />}
                disabled={!enabled}
                onClick={() => enabled && onManageTags?.(record)}
                style={{ width: "100%" }}
              >
                Tags
              </Button>
            </Badge>
          </Tooltip>
          <Tooltip title={reason || "Manage bucket"}>
            <Button
              size="small"
              icon={<AppstoreAddOutlined />}
              disabled={!enabled}
              onClick={() => enabled && onManageBucket?.(record)}
              style={{ width: "100%" }}
            >
              Bucket
            </Button>
          </Tooltip>
       
        </Space>
      );
    },
  },
];

export const buildAssignmentColumns = ({ bucketsById = {}, onManageBucket }) => [
  {
    title: "Default",
    dataIndex: "isDefault",
    key: "isDefault",
    width: 90,
    fixed: "left",
    align: "center",
    render: (v) =>
      Number(v) === 1 ? (
        <Tag color="gold" icon={<StarFilled />}>
          Default
        </Tag>
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
  },
  {
    title: "Variant ID",
    dataIndex: "variantId",
    key: "variantId",
    width: 130,
    fixed: "left",
    render: (v) => <Typography.Text code>{v}</Typography.Text>,
  },
  {
    title: "Bucket",
    dataIndex: "bucketId",
    key: "bucketId",
    width: 260,
    render: (id) => {
      const b = bucketsById[String(id)];
      if (!b) return <Typography.Text type="secondary">bucket #{id}</Typography.Text>;
      return (
        <Space size={6}>
          <Tag color="green" style={{ marginRight: 0 }}>
            {b.bucketKey}
          </Tag>
          <Typography.Text>{b.displayName}</Typography.Text>
        </Space>
      );
    },
  },
  {
    title: "Store",
    dataIndex: "storeHash",
    key: "storeHash",
    width: 170,
    render: (hash, row) => {
      const lbl = row.storeLabel || labelForHash(hash);
      return (
        <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
          {lbl ? (
            <Tag color="geekblue" style={{ marginRight: 0 }}>
              {lbl}
            </Tag>
          ) : null}
          <Typography.Text code style={{ fontSize: 11 }}>
            {hash}
          </Typography.Text>
        </Space>
      );
    },
  },
  {
    title: "Created",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 170,
    render: formatDate,
  },
  {
    title: "Updated",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 170,
    render: formatDate,
  },
  {
    title: "Actions",
    key: "actions",
    width: 130,
    fixed: "right",
    align: "center",
    render: (_, row) => {
      const variantOk = isValidVariantId(row.variantId);
      const storeOk = !!row.storeHash;
      const enabled = variantOk && storeOk;
      return (
        <Tooltip title={enabled ? "Manage bucket" : "Cannot manage — bad row data"}>
          <Button
            size="small"
            icon={<AppstoreAddOutlined />}
            disabled={!enabled}
            onClick={() => enabled && onManageBucket?.(row)}
          >
            Manage
          </Button>
        </Tooltip>
      );
    },
  },
];

export default buildColumns;
